/**
 * Utilidades para manejo de fechas y horas en timezone de Lima (America/Lima)
 */

/**
 * Extrae año, mes y día de un valor date-only (YYYY-MM-DD o ISO con hora).
 */
export const parseDateOnlyParts = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
  }
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }
  return null;
};

/**
 * Formatear fecha a formato YYYY-MM-DD en timezone de Lima
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const formatDateToLima = (date) => {
  if (!date) return '';
  try {
    const parts = parseDateOnlyParts(date);
    if (parts) {
      const { year, month, day } = parts;
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '';

    const limaString = dateObj.toLocaleString('en-US', { timeZone: 'America/Lima' });
    const limaDate = new Date(limaString);
    const year = limaDate.getFullYear();
    const month = String(limaDate.getMonth() + 1).padStart(2, '0');
    const day = String(limaDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return '';
  }
};

/**
 * Formatear fecha a formato localizado (es-PE) en timezone de Lima
 * @param {Date|string} date - Fecha a formatear
 * @param {object} options - Opciones de formato
 * @returns {string} Fecha formateada
 */
export const formatDateLocaleLima = (date, options = {}) => {
  if (!date) return '';
  try {
    const parts = parseDateOnlyParts(date);
    let dateObj;

    if (parts) {
      dateObj = new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0, 0);
    } else {
      dateObj = typeof date === 'string' ? new Date(date) : date;
    }

    if (Number.isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleDateString('es-PE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/Lima',
      ...options,
    });
  } catch (error) {
    console.error('Error formateando fecha localizada:', error);
    return '';
  }
};

/**
 * Formatear hora a formato HH:mm en timezone de Lima
 * @param {Date|string} date - Fecha/hora a formatear
 * @returns {string} Hora en formato HH:mm
 */
export const formatTimeToLima = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    
    // Obtener hora en timezone de Lima
    const limaString = dateObj.toLocaleString('en-US', { 
      timeZone: 'America/Lima',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
    // Si ya viene en formato HH:mm, usarlo directamente
    if (typeof date === 'string' && /^\d{2}:\d{2}/.test(date)) {
      return date.substring(0, 5);
    }
    // Extraer hora y minutos del string formateado
    const match = limaString.match(/(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    // Fallback: usar métodos de Date
    const limaDate = new Date(dateObj.toLocaleString('en-US', { timeZone: 'America/Lima' }));
    const hours = String(limaDate.getHours()).padStart(2, '0');
    const minutes = String(limaDate.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formateando hora:', error);
    return '';
  }
};

/**
 * Hora para API de kardex/ajustes: HH:mm; si vacía, 23:59 (fin del día).
 */
export const normalizeTimeForApi = (time, { endOfDayIfEmpty = true } = {}) => {
  const raw = String(time ?? '').trim();
  if (!raw) {
    return endOfDayIfEmpty ? '23:59' : null;
  }
  const match = raw.match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : (endOfDayIfEmpty ? '23:59' : null);
};

/**
 * Obtener la hora actual en formato HH:mm en timezone de Lima
 * @returns {string} Hora actual en formato HH:mm
 */
export const getCurrentTimeLima = () => {
  try {
    const now = new Date();
    const limaString = now.toLocaleString('en-US', { 
      timeZone: 'America/Lima',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
    const match = limaString.match(/(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    // Fallback
    const limaDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Lima' }));
    const hours = String(limaDate.getHours()).padStart(2, '0');
    const minutes = String(limaDate.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (error) {
    console.error('Error obteniendo hora actual:', error);
    return '';
  }
};

/**
 * Obtener la fecha actual en formato YYYY-MM-DD en timezone de Lima
 * @returns {string} Fecha actual en formato YYYY-MM-DD
 */
export const getCurrentDateLima = () => {
  return formatDateToLima(new Date());
};

/**
 * Convertir string de hora (HH:mm o HH:mm:ss) a formato HH:mm
 * @param {string} timeString - String de hora
 * @returns {string} Hora en formato HH:mm
 */
export const normalizeTime = (timeString) => {
  if (!timeString) return '';
  if (timeString.includes(':')) {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
  }
  return timeString;
};

/**
 * Fecha de ingreso de guía + hora (kardex), en formato local Lima.
 */
export const formatGuideEntryDateTimeLima = (entryDate, entryTime) => {
  const dateStr = formatDateLocaleLima(entryDate);
  if (!dateStr) return '—';
  const time = normalizeTime(entryTime);
  return time ? `${dateStr} ${time}` : dateStr;
};

const buildIsoDate = (year, month, day) => {
  const d = new Date(year, month - 1, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Convierte YYYY-MM-DD a DD/MM/AAAA para mostrar en inputs de texto.
 */
export const isoDateToDisplay = (iso) => {
  const parts = parseDateOnlyParts(iso);
  if (!parts) return '';
  const { year, month, day } = parts;
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
};

/**
 * Interpreta fechas escritas (DD/MM/AAAA, DD-MM-AAAA, AAAA-MM-DD) y devuelve YYYY-MM-DD.
 */
export const parseFlexibleDateInput = (input) => {
  if (input === null || input === undefined) return null;
  const s = String(input).trim();
  if (!s) return null;

  let match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    return buildIsoDate(Number(match[1]), Number(match[2]), Number(match[3]));
  }

  match = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (match) {
    return buildIsoDate(Number(match[3]), Number(match[2]), Number(match[1]));
  }

  return null;
};

/**
 * Interpreta horas escritas (HH:mm, H:mm, HHmm) y devuelve HH:mm.
 */
export const parseFlexibleTimeInput = (input) => {
  if (input === null || input === undefined) return null;
  const s = String(input).trim();
  if (!s) return null;

  let match = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    return null;
  }

  match = s.match(/^(\d{1,2})(\d{2})$/);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
  }

  return null;
};

